import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Card, Container } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiRefreshCcw } from 'react-icons/fi';
import { RiSparkling2Fill } from 'react-icons/ri';

import Exam from '../components/data/Exam';
import LearningContextPreview from '../components/ui/LearningContextPreview';
import {
  fetchExamQuestions,
  resetExam,
  selectExamState,
  setQuestionIndex,
  startExam,
} from '../redux/examSlice';
import {
  selectActiveCourse as selectClassroomCourse,
  selectActiveLesson,
} from '../redux/classroomSlice';
import { selectActiveCourse as selectCoursesActiveCourse } from '../redux/coursesSlice';
import { selectProfile } from '../redux/profileSlice';
import '../components/data/Classroom.css';

const normalizeObjectives = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const CourseExamsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useSelector(selectProfile);
  const classroomCourse = useSelector(selectClassroomCourse);
  const coursesActiveCourse = useSelector(selectCoursesActiveCourse);
  const activeLesson = useSelector(selectActiveLesson);
  const examState = useSelector(selectExamState);
  const [mobilePane, setMobilePane] = useState('exam');
  const lastRequestKeyRef = useRef(null);
  const { questions, currentQuestionIndex, studentAnswers, result } = examState;
  const perQuestionResults = Array.isArray(result?.perQuestion) ? result.perQuestion : [];

  const routeState = useMemo(() => location.state || {}, [location.state]);
  const resolvedCourse = routeState.courseId
    ? null
    : classroomCourse || coursesActiveCourse || null;
  const resolvedUnit = routeState.unitId ? null : activeLesson || null;

  const resolvedCourseId = routeState.courseId || resolvedCourse?.id || null;
  const resolvedCourseTitle = routeState.courseTitle || resolvedCourse?.title || '';
  const resolvedUnitId = routeState.unitId || resolvedUnit?.id || null;
  const resolvedUnitTitle =
    routeState.unitTitle || resolvedUnit?.title || resolvedUnit?.unit_title || '';
  const resolvedUnitObjective =
    routeState.unitObjective || resolvedUnit?.objective || resolvedUnit?.unit_objective || '';
  const courseObjectives = useMemo(
    () => normalizeObjectives(resolvedCourse?.objectives),
    [resolvedCourse?.objectives]
  );

  const lessonTitle = useMemo(
    () => resolvedUnitTitle || resolvedCourseTitle || 'Course Exam',
    [resolvedCourseTitle, resolvedUnitTitle]
  );
  const showExamContext = examState.loading || (!questions.length && !examState.error);

  useEffect(() => {
    if (!resolvedCourseId) {
      navigate('/dashboard');
      return;
    }

    const requestKey = JSON.stringify({
      courseId: resolvedCourseId,
      unitId: resolvedUnitId,
      lessonTitle,
      userId: profile?.id || null,
    });

    if (lastRequestKeyRef.current === requestKey) {
      return;
    }
    lastRequestKeyRef.current = requestKey;

    dispatch(resetExam());
    dispatch(startExam());
    dispatch(
      fetchExamQuestions({
        lessonTitle,
        questionCount: 10,
        studentProfile: profile || null,
        courseDetails: {
          id: resolvedCourseId,
          title: resolvedCourseTitle,
          unitId: resolvedUnitId,
          unitTitle: resolvedUnitTitle,
          objective: resolvedUnitObjective,
        },
      })
    );
  }, [
    dispatch,
    lessonTitle,
    navigate,
    profile,
    resolvedCourseId,
    resolvedCourseTitle,
    resolvedUnitId,
    resolvedUnitObjective,
    resolvedUnitTitle,
  ]);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

  const restartExam = () => {
    lastRequestKeyRef.current = null;
    dispatch(resetExam());
    dispatch(startExam());
    dispatch(
      fetchExamQuestions({
        lessonTitle,
        questionCount: 10,
        studentProfile: profile || null,
        courseDetails: {
          id: resolvedCourseId,
          title: resolvedCourseTitle,
          unitId: resolvedUnitId,
          unitTitle: resolvedUnitTitle,
          objective: resolvedUnitObjective,
        },
      })
    );
  };

  const formatAnswer = (value) => {
    if (Array.isArray(value)) return value.join(', ');
    if (value && typeof value === 'object') return JSON.stringify(value);
    return String(value ?? '');
  };

  return (
    <Container className="classroom course-exam-page">
      <div className="classroom-header d-flex align-items-center justify-content-between gap-2 exam-page-header">
        <div className="exam-page-header__title-wrap">
          <h1 className="lesson-title d-flex align-items-center gap-2 mb-1">
            <RiSparkling2Fill className="course-exam-ai-icon" />
            {lessonTitle}
          </h1>
          <p className="text-muted mb-0">Exam Mode</p>
        </div>
        <div className="d-flex align-items-center gap-2 exam-page-header__actions">
          <button
            type="button"
            className="exam-icon-button"
            onClick={restartExam}
            aria-label="Restart exam"
            title="Restart exam"
          >
            <FiRefreshCcw />
          </button>
          <button
            type="button"
            className="exam-icon-button"
            onClick={() => navigate('/dashboard')}
            aria-label="Back to dashboard"
            title="Back to dashboard"
          >
            <FiArrowLeft />
          </button>
        </div>
      </div>

      <div className="classroom-content course-exam-workspace">
        <Card
          className={`component-card exam-main-card ${mobilePane !== 'exam' ? 'exam-mobile-hidden' : ''}`}
        >
          <Card.Body>
            <div className="exam-mobile-toggle" role="tablist" aria-label="Exam mobile view toggle">
              <button
                type="button"
                className={`exam-mobile-toggle-btn ${mobilePane === 'exam' ? 'is-active' : ''}`}
                onClick={() => setMobilePane('exam')}
                aria-label="Show exam content"
              >
                Exam
              </button>
              <button
                type="button"
                className={`exam-mobile-toggle-btn ${mobilePane === 'list' ? 'is-active' : ''}`}
                onClick={() => setMobilePane('list')}
                aria-label="Show question list"
              >
                Questions
              </button>
            </div>
            <h3 className="component-title">Exam</h3>
            {examState.error ? <Alert variant="danger">{examState.error}</Alert> : null}
            {showExamContext ? (
              <LearningContextPreview
                courseTitle={resolvedCourseTitle || 'Untitled Course'}
                lessonTitle={lessonTitle}
                lessonObjective={resolvedUnitObjective || 'No unit objective provided.'}
                courseObjectives={courseObjectives}
                activityLabel="Exam"
                eyebrow="Preparing your exam context"
                subtitle="We're grounding this assessment in the current course, unit, and objective before the questions begin."
                loadingMessage="Building exam questions for this unit..."
              />
            ) : (
              <Exam />
            )}
          </Card.Body>
        </Card>

        {!showExamContext ? (
          <Card
            className={`chat-card exam-sidebar-card ${mobilePane !== 'list' ? 'exam-mobile-hidden' : ''}`}
          >
            <Card.Body>
              <div
                className="exam-mobile-toggle"
                role="tablist"
                aria-label="Exam mobile view toggle"
              >
                <button
                  type="button"
                  className={`exam-mobile-toggle-btn ${mobilePane === 'exam' ? 'is-active' : ''}`}
                  onClick={() => setMobilePane('exam')}
                  aria-label="Show exam content"
                >
                  Exam
                </button>
                <button
                  type="button"
                  className={`exam-mobile-toggle-btn ${mobilePane === 'list' ? 'is-active' : ''}`}
                  onClick={() => setMobilePane('list')}
                  aria-label="Show question list"
                >
                  Questions
                </button>
              </div>
              <h3 className="component-title">Questions</h3>
              <div className="exam-sidebar-list exam-sidebar-list--standalone">
                {questions.map((question, index) => {
                  const qid = question.question_id;
                  const answered = studentAnswers[qid] !== undefined && studentAnswers[qid] !== '';
                  const active = index === currentQuestionIndex;
                  const questionResult = perQuestionResults.find((item) => item.questionId === qid);
                  return (
                    <button
                      key={qid || `q_${index + 1}`}
                      type="button"
                      className={`exam-sidebar-item ${active ? 'is-active' : ''}`}
                      onClick={() => {
                        dispatch(setQuestionIndex(index));
                        setMobilePane('exam');
                      }}
                      aria-label={`Go to question ${index + 1}`}
                    >
                      <span className="exam-sidebar-item-index">{index + 1}</span>
                      <span className="exam-sidebar-item-meta">
                        <span>Q{index + 1}</span>
                        <span>{question.points} pts</span>
                      </span>
                      <span
                        className={`exam-sidebar-item-state ${answered ? 'is-answered' : ''} ${questionResult ? 'is-scored' : ''}`}
                      >
                        {questionResult
                          ? `${questionResult.earnedPoints}/${questionResult.maxPoints}`
                          : answered
                            ? 'Answered'
                            : 'Pending'}
                      </span>
                    </button>
                  );
                })}
              </div>

              {questions[currentQuestionIndex] && perQuestionResults.length > 0 ? (
                <div className="exam-sidebar-score-detail">
                  <h4 className="exam-sidebar-list-title mb-1">Selected Question Result</h4>
                  {(() => {
                    const qid = questions[currentQuestionIndex].question_id;
                    const selected = perQuestionResults.find((item) => item.questionId === qid);
                    if (!selected) return <p className="mb-0 text-muted">Not graded yet.</p>;
                    return (
                      <>
                        <p className="mb-1">
                          <strong>
                            Score: {selected.earnedPoints}/{selected.maxPoints}
                          </strong>
                        </p>
                        {selected.expectedAnswer !== undefined ? (
                          <p className="mb-1 text-muted">
                            <strong>Correct answer:</strong> {formatAnswer(selected.expectedAnswer)}
                          </p>
                        ) : null}
                        {selected.feedback ? (
                          <p className="mb-0 text-muted">{selected.feedback}</p>
                        ) : null}
                      </>
                    );
                  })()}
                </div>
              ) : null}
            </Card.Body>
          </Card>
        ) : null}
      </div>
    </Container>
  );
};

export default CourseExamsPage;
